import { EMPTY_OBJECT } from '../../../../../constants/common'
import { useMassActions, useNotification } from '../../../../../hooks'
import { getMassActionsSuccessItemsLength } from '../../../../../utils/massActions'
import { UserProfileTabId, useUserProfileContext } from '../../../../User'

import {
  getUserProfilePagePerformActionNotificationTitle,
  getUserProfilePageStatusUpdateNotificationTitleKey,
  performInhabitantUserProfileMassAction,
  performStaffUserProfileMassAction,
} from './useUserProfilePagePerformAction.utils'

export function useUserProfilePagePerformAction() {
  const {
    handleCloseModal,
    handleEntityReload,
    handleReloadTabs,
    handleUpdateEntity,
    setPerformingAction,
    userData,
  } = useUserProfileContext()

  const { handleAddErrorNotification } = useNotification()

  // Archiving/restoring a user archives/restores their contracts as well.
  async function handleReloadAfterAction() {
    return Promise.all([
      handleEntityReload(),
      handleReloadTabs([UserProfileTabId.Contracts]),
    ])
  }

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getNotificationTitle: getUserProfilePagePerformActionNotificationTitle,
    onSuccessHandler: handleReloadAfterAction,
  })

  // TODO: staff goes through the old endpoints until `inhabitants/mass-action/`
  // supports admins and managers; then drop performStaffUserProfileMassAction.
  const performMassAction = userData?.isStaff
    ? performStaffUserProfileMassAction
    : performInhabitantUserProfileMassAction

  function handlePerformMassAction({
    action,
    isArchived = userData.isArchived,
    payload = EMPTY_OBJECT,
    onSuccess,
  }) {
    handleCloseModal()
    setPerformingAction(true)

    return performMassAction({ action, id: userData.id, isArchived, payload })
      .then(data => {
        const asyncTask = getAsyncTaskId(data)

        if (asyncTask) {
          handleAddAsyncMassActionNotification({
            action,
            task: asyncTask,
            totalCount: 1,
          })

          return
        }

        handleAddFinishMassActionNotification(data, {
          action,
          totalCount: 1,
        })

        return handleReloadAfterAction().then(() => {
          if (getMassActionsSuccessItemsLength(data) > 0) {
            onSuccess?.()
          }
        })
      })
      .catch(() => {
        handleAddErrorNotification('Notification.InhabitantMassActionErrorKey')
      })
      .finally(() => {
        setPerformingAction(false)
      })
  }

  function handleUpdateStatus(status) {
    handleCloseModal()
    setPerformingAction(true)

    return handleUpdateEntity(
      { status },
      getUserProfilePageStatusUpdateNotificationTitleKey(status)
    )
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
      .finally(() => {
        setPerformingAction(false)
      })
  }

  return {
    handlePerformMassAction,
    handleUpdateStatus,
  }
}
